import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, CheckCircle, XCircle, Loader2, RefreshCw, Eye, EyeOff, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

interface SupplierIntegration {
  id: string;
  supplier_name: string;
  client_id: string | null;
  api_key: string | null;
  customer_code: string | null;
  preferred_branch_code: string | null;
  preferred_branch_name: string | null;
  is_active: boolean;
  last_sync_at: string | null;
  sync_status: string | null;
  products_count: number | null;
}

interface Branch {
  code: string;
  name: string;
  address: string;
}

interface SupplierIntegrationsPanelProps {
  orgId: string | null;
}

export function SupplierIntegrationsPanel({ orgId }: SupplierIntegrationsPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  
  const [integration, setIntegration] = useState<SupplierIntegration | null>(null);
  const [formData, setFormData] = useState({
    clientId: "",
    clientSecret: "",
    customerCode: "",
    preferredBranchCode: "",
    preferredBranchName: "",
  });

  useEffect(() => {
    if (orgId) {
      loadIntegration();
    }
  }, [orgId]);

  const loadIntegration = async () => {
    try {
      const { data, error } = await supabase
        .from("supplier_integrations")
        .select("*")
        .eq("org_id", orgId)
        .eq("supplier_name", "roofhub")
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setIntegration({
          id: data.id,
          supplier_name: data.supplier_name,
          client_id: data.client_id,
          api_key: data.api_key,
          customer_code: data.customer_code,
          preferred_branch_code: data.preferred_branch_code,
          preferred_branch_name: data.preferred_branch_name,
          is_active: data.is_active ?? false,
          last_sync_at: data.last_sync_at,
          sync_status: data.sync_status,
          products_count: data.products_count,
        });
        setFormData({
          clientId: data.client_id || "",
          clientSecret: data.api_key || "",
          customerCode: data.customer_code || "",
          preferredBranchCode: data.preferred_branch_code || "",
          preferredBranchName: data.preferred_branch_name || "",
        });
        
        // If connected, load branches
        if (data.is_active && data.client_id && data.api_key) {
          loadBranches();
        }
      }
    } catch (error) {
      console.error("Error loading integration:", error);
      toast.error("Failed to load supplier integration");
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    setLoadingBranches(true);
    try {
      const response = await supabase.functions.invoke("roofhub-pricing", {
        body: { action: "get-branches" },
      });

      if (response.error) throw response.error;
      setBranches(response.data?.branches || []);
    } catch (error) {
      console.error("Error loading branches:", error);
    } finally {
      setLoadingBranches(false);
    }
  };

  const handleTestConnection = async () => {
    if (!formData.clientId || !formData.clientSecret || !formData.customerCode) {
      toast.error("Please enter your Client ID, Client Secret, and Customer Code");
      return;
    }

    setTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke("roofhub-pricing", {
        body: {
          action: "test-connection",
          clientId: formData.clientId,
          clientSecret: formData.clientSecret,
          customerCode: formData.customerCode,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success(data.message);
        if (data.branches) {
          setBranches(data.branches);
        }
      } else {
        toast.error(data?.message || "Connection test failed");
      }
    } catch (error) {
      console.error("Connection test failed:", error);
      toast.error("Failed to test connection");
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!orgId) return;

    setSaving(true);
    try {
      const integrationData = {
        org_id: orgId,
        supplier_name: "roofhub",
        client_id: formData.clientId,
        api_key: formData.clientSecret, // Store client_secret in api_key field
        customer_code: formData.customerCode,
        preferred_branch_code: formData.preferredBranchCode,
        preferred_branch_name: formData.preferredBranchName,
        is_active: true,
      };

      if (integration?.id) {
        const { error } = await supabase
          .from("supplier_integrations")
          .update(integrationData)
          .eq("id", integration.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("supplier_integrations")
          .insert(integrationData);

        if (error) throw error;
      }

      toast.success("Supplier integration saved");
      await loadIntegration();
    } catch (error) {
      console.error("Error saving integration:", error);
      toast.error("Failed to save integration");
    } finally {
      setSaving(false);
    }
  };

  const handleSyncProducts = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("roofhub-pricing", {
        body: { action: "sync-products" },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success(data.message);
        await loadIntegration();
      } else {
        toast.error(data?.error || "Failed to sync products");
      }
    } catch (error) {
      console.error("Sync failed:", error);
      toast.error("Failed to sync products");
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!integration?.id) return;

    try {
      const { error } = await supabase
        .from("supplier_integrations")
        .update({ is_active: false })
        .eq("id", integration.id);

      if (error) throw error;

      toast.success("Disconnected from RoofHub");
      await loadIntegration();
    } catch (error) {
      console.error("Error disconnecting:", error);
      toast.error("Failed to disconnect");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* RoofHub Integration Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Truck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">SRS Distribution (RoofHub)</CardTitle>
                <CardDescription>
                  Connect your SRS account to get real-time pricing and sync your full product catalog
                </CardDescription>
              </div>
            </div>
            <Badge variant={integration?.is_active ? "default" : "secondary"}>
              {integration?.is_active ? (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Connected
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3 mr-1" />
                  Not Connected
                </>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 rounded-lg bg-muted/50 text-sm">
            <p className="text-muted-foreground">
              Need credentials? Contact SRS at{" "}
              <a 
                href="mailto:APISupportTeam@srsdistribution.com" 
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                APISupportTeam@srsdistribution.com
                <ExternalLink className="w-3 h-3" />
              </a>
              {" "}to request your Client ID and Client Secret.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="clientId">Client ID</Label>
              <Input
                id="clientId"
                type="text"
                placeholder="Your SRS Client ID"
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clientSecret">Client Secret</Label>
              <div className="relative">
                <Input
                  id="clientSecret"
                  type={showClientSecret ? "text" : "password"}
                  placeholder="Your SRS Client Secret"
                  value={formData.clientSecret}
                  onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowClientSecret(!showClientSecret)}
                >
                  {showClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="customerCode">Customer Code (Account Number)</Label>
            <Input
              id="customerCode"
              type="text"
              placeholder="Your SRS account number"
              value={formData.customerCode}
              onChange={(e) => setFormData({ ...formData, customerCode: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="preferredBranch">Preferred Branch (Your Local NJ Branch)</Label>
            <Select
              value={formData.preferredBranchCode}
              onValueChange={(value) => {
                const branch = branches.find(b => b.code === value);
                setFormData({
                  ...formData,
                  preferredBranchCode: value,
                  preferredBranchName: branch?.name || "",
                });
              }}
              disabled={branches.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder={
                  loadingBranches 
                    ? "Loading branches..." 
                    : branches.length === 0 
                      ? "Test connection to load branches" 
                      : "Select your local branch"
                } />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {branches.map((branch) => (
                  <SelectItem key={branch.code} value={branch.code}>
                    <span className="font-medium">{branch.name}</span>
                    <span className="text-muted-foreground text-xs ml-2">({branch.code})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.preferredBranchName && (
              <p className="text-xs text-muted-foreground">
                Selected: {formData.preferredBranchName}
              </p>
            )}
          </div>

          {integration?.is_active && integration.last_sync_at && (
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  Last synced {formatDistanceToNow(new Date(integration.last_sync_at))} ago
                </span>
                <span className="font-medium text-primary">
                  {integration.products_count?.toLocaleString()} products
                </span>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing || !formData.clientId || !formData.clientSecret || !formData.customerCode}
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                "Test Connection"
              )}
            </Button>

            {integration?.is_active && (
              <Button
                variant="outline"
                onClick={handleSyncProducts}
                disabled={syncing || !formData.preferredBranchCode}
              >
                {syncing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Syncing Full Catalog...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sync Products
                  </>
                )}
              </Button>
            )}

            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Integration"
              )}
            </Button>

            {integration?.is_active && (
              <Button variant="destructive" onClick={handleDisconnect}>
                Disconnect
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Coming Soon Card */}
      <Card className="border-dashed">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Plus className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-lg text-muted-foreground">More Suppliers Coming Soon</CardTitle>
              <CardDescription>
                ABC Supply, Beacon Roofing, and more integrations are in development
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>
  );
}
